// AnnualReports — admin /reports/annual (CHG-1659).
//
// Spec authority: specs/tier-05-experience/annual-reports.entity-hint.yaml
// (pages.dashboard_v2 block). Documented hand-roll exception per DRIFT-702.

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { DashboardPage } from "../components/composition/DashboardPage";
import {
  StatStrip,
  type StatStripItem,
} from "../components/composition/StatStrip";
import { useFormatters } from "../contexts/IntlContext";
import { reportsApi, type AnnualReport } from "../api/reports";

const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function statsFromAnnual(data: AnnualReport | undefined): StatStripItem[] {
  return [
    {
      id: "total_raised",
      label: "Raised this year",
      value: data?.total_raised_minor_units ?? null,
      format: "currency",
      currency: "USD",
      tone: "g-giving",
    },
    {
      id: "total_donations",
      label: "Donations",
      value: data?.total_donations ?? null,
      format: "number",
      tone: "g-giving",
    },
    {
      id: "unique_donors",
      label: "Donors",
      value: data?.unique_donors ?? null,
      format: "number",
      tone: "g-people",
    },
  ];
}

function YearPicker({
  year,
  onChange,
}: {
  year: number;
  onChange: (year: number) => void;
}) {
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - i);
  return (
    <select
      value={year}
      onChange={(e) => onChange(parseInt(e.target.value, 10))}
      style={{
        height: 32,
        padding: "0 12px",
        borderRadius: 6,
        border: "1px solid var(--color-border)",
        background: "var(--color-surface)",
        color: "var(--color-text)",
        fontSize: 13,
      }}
      aria-label="Year"
    >
      {years.map((y) => (
        <option key={y} value={y}>
          {y}
        </option>
      ))}
    </select>
  );
}

function MonthlyBreakdownCard({
  data,
}: {
  data: AnnualReport | undefined;
}) {
  const { formatCurrency } = useFormatters();
  if (!data || data.by_month.length === 0) {
    return null;
  }
  const max = data.by_month.reduce((m, r) => Math.max(m, r.total_minor_units), 0);
  return (
    <section
      className="card"
      aria-labelledby="monthly-breakdown-title"
      style={{ padding: 16 }}
    >
      <h2
        id="monthly-breakdown-title"
        style={{
          fontSize: 12,
          fontWeight: 500,
          margin: "0 0 12px",
          color: "var(--color-text-muted)",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        Monthly breakdown
      </h2>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "auto 1fr auto",
          gap: "4px 12px",
          alignItems: "center",
          fontSize: 12,
        }}
      >
        {data.by_month.map((row) => {
          const pct = max > 0 ? (row.total_minor_units / max) * 100 : 0;
          return (
            <div key={row.month} style={{ display: "contents" }}>
              <span style={{ color: "var(--color-text-muted)" }}>
                {MONTH_LABELS[row.month - 1] ?? row.month}
              </span>
              <div
                aria-hidden="true"
                style={{
                  height: 6,
                  background: "var(--color-border)",
                  borderRadius: 3,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${pct}%`,
                    background: "var(--accent, #0066cc)",
                  }}
                />
              </div>
              <span style={{ fontVariantNumeric: "tabular-nums" }}>
                {formatCurrency(row.total_minor_units, "USD")}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function TopDonorsCard({ data }: { data: AnnualReport | undefined }) {
  const { formatCurrency } = useFormatters();
  const donors = data?.top_donors ?? [];
  if (donors.length === 0) return null;
  return (
    <section
      className="card"
      aria-labelledby="top-donors-title"
      style={{ padding: 16 }}
    >
      <h2
        id="top-donors-title"
        style={{
          fontSize: 12,
          fontWeight: 500,
          margin: "0 0 12px",
          color: "var(--color-text-muted)",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        Top donors
      </h2>
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {donors.slice(0, 10).map((d) => (
          <li
            key={d.donor_id}
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "6px 0",
              fontSize: 13,
              borderBottom: "1px solid var(--color-border)",
            }}
          >
            <span>{d.name}</span>
            <span
              style={{
                fontVariantNumeric: "tabular-nums",
                color: "var(--color-text-muted)",
              }}
            >
              {formatCurrency(d.total_minor_units, "USD")}
              <span style={{ opacity: 0.5 }}> ({d.donation_count})</span>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function AnnualReports() {
  const [year, setYear] = useState(new Date().getFullYear());
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["reports", "annual", year] as const,
    queryFn: () => reportsApi.fetchAnnualReport(year),
  });
  return (
    <DashboardPage
      title="Annual reports"
      subtitle="Year-by-year giving totals with monthly breakdown, source mix, top donors."
      headerActions={[
        {
          id: "year-picker",
          label: "Year",
          // PageHeaderAction primary is button-shaped, but its render slot
          // accepts arbitrary ReactNode via the kind:"custom" path. We
          // sidestep that and render the picker via the page subtitle slot
          // instead to keep this hand-roll minimal.
          onClick: () => {},
          variant: "ghost",
        },
      ]}
      stats={<StatStrip items={statsFromAnnual(data)} loading={isLoading} />}
      feed={
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: 12,
            }}
          >
            <YearPicker year={year} onChange={setYear} />
          </div>
          <MonthlyBreakdownCard data={data} />
        </>
      }
      callout={<TopDonorsCard data={data} />}
      loading={isLoading}
      error={error as Error | null}
      onRetry={() => refetch()}
    />
  );
}
